import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Plus, FileText, Clock, CheckCircle2, AlertCircle, Upload,
  Loader2, Trash2, ChevronDown, ChevronRight, Send, ArrowLeft,
  Star, RotateCcw, Paperclip, Calendar, Award, X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_LABELS = { tarea: 'Tarea', trabajo_practico: 'Trabajo Práctico', informe: 'Informe' };

function formatFecha(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatFechaCorta(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function CampusTareas({ spaceId, esCoordinadorODocente, token }) {
  const headers = { Authorization: `Bearer ${token}` };

  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titulo: '', descripcion: '', tipo: 'tarea',
    fecha_vencimiento: '', puntos_maximo: 100,
    permite_entrega_tardia: false, estado: 'publicada'
  });

  // Detail view
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [loadingEntregas, setLoadingEntregas] = useState(false);

  // Submission form
  const [submissionText, setSubmissionText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Grading
  const [gradingId, setGradingId] = useState(null);
  const [gradeForm, setGradeForm] = useState({ nota: '', comentario: '' });
  const [grading, setGrading] = useState(false);

  const fetchTareas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/tareas`, { headers });
      setTareas(res.data);
    } catch (err) {
      console.error('Error cargando tareas:', err);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => { fetchTareas(); }, [fetchTareas]);

  const crearTarea = async () => {
    if (!form.titulo.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        puntos_maximo: parseInt(form.puntos_maximo) || 100,
        fecha_vencimiento: form.fecha_vencimiento
          ? new Date(form.fecha_vencimiento).toISOString()
          : null
      };
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/tareas`, payload, { headers });
      setShowForm(false);
      setForm({ titulo: '', descripcion: '', tipo: 'tarea', fecha_vencimiento: '', puntos_maximo: 100, permite_entrega_tardia: false, estado: 'publicada' });
      fetchTareas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear tarea');
    } finally {
      setSaving(false);
    }
  };

  const eliminarTarea = async (tareaId) => {
    if (!window.confirm('¿Eliminar esta tarea y todas sus entregas?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/tareas/${tareaId}`, { headers });
      if (selectedTarea?.id === tareaId) setSelectedTarea(null);
      fetchTareas();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const openTarea = async (tarea) => {
    setSelectedTarea(tarea);
    setLoadingEntregas(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/tareas/${tarea.id}/entregas`, { headers });
      setEntregas(res.data);
    } catch (err) {
      console.error('Error cargando entregas:', err);
    } finally {
      setLoadingEntregas(false);
    }
  };

  const entregarTarea = async () => {
    if (!submissionText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/tareas/${selectedTarea.id}/entregar`,
        { contenido: submissionText, archivos: [] },
        { headers }
      );
      setSubmissionText('');
      openTarea(selectedTarea);
      fetchTareas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al entregar');
    } finally {
      setSubmitting(false);
    }
  };

  const calificarEntrega = async (entregaId) => {
    const nota = parseInt(gradeForm.nota);
    if (isNaN(nota)) { alert('Ingresá una nota válida'); return; }
    setGrading(true);
    try {
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/tareas/${selectedTarea.id}/entregas/${entregaId}/calificar`,
        { nota, comentario: gradeForm.comentario },
        { headers }
      );
      setGradingId(null);
      setGradeForm({ nota: '', comentario: '' });
      openTarea(selectedTarea);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al calificar');
    } finally {
      setGrading(false);
    }
  };

  const devolverEntrega = async (entregaId) => {
    try {
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/tareas/${selectedTarea.id}/entregas/${entregaId}/devolver`,
        {},
        { headers }
      );
      openTarea(selectedTarea);
    } catch (err) {
      alert('Error al devolver');
    }
  };

  // --- Detail View ---
  if (selectedTarea) {
    const t = selectedTarea;
    const miEntrega = entregas.find(e => !esCoordinadorODocente) || t.mi_entrega;
    const puedeEntregar = !esCoordinadorODocente && t.estado === 'publicada' &&
      (!t.mi_entrega || t.mi_entrega.estado === 'devuelto') &&
      (!t.vencida || t.permite_entrega_tardia);

    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setSelectedTarea(null)} data-testid="back-to-tareas">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver a tareas
        </Button>

        {/* Task header */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs capitalize">{TIPO_LABELS[t.tipo] || t.tipo}</Badge>
                  <EstadoBadge estado={t.estado} vencida={t.vencida} />
                </div>
                <h2 className="text-xl font-bold" data-testid="tarea-detail-title">{t.titulo}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Creada por {t.autor?.nombre} {t.autor?.apellido} · {formatFecha(t.created_at)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-violet-600">{t.puntos_maximo}</div>
                <div className="text-xs text-gray-500">puntos</div>
              </div>
            </div>

            {t.descripcion && (
              <p className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">{t.descripcion}</p>
            )}

            <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
              {t.fecha_vencimiento && (
                <span className={`flex items-center gap-1 ${t.vencida ? 'text-red-500' : ''}`}>
                  <Clock className="w-3.5 h-3.5" />
                  Vence: {formatFechaCorta(t.fecha_vencimiento)}
                </span>
              )}
              {t.permite_entrega_tardia && (
                <span className="text-amber-600">Acepta entregas tardías</span>
              )}
              <span>{t.total_entregas || entregas.length} entrega(s)</span>
            </div>

            {t.archivos_adjuntos?.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {t.archivos_adjuntos.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded">
                    <Paperclip className="w-3 h-3" />{a.nombre || 'Archivo'}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Student submission area */}
        {!esCoordinadorODocente && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tu entrega</CardTitle>
            </CardHeader>
            <CardContent>
              {t.mi_entrega?.estado === 'calificado' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-violet-600">{t.mi_entrega.nota}</div>
                    <div className="text-sm text-gray-500">/ {t.puntos_maximo}</div>
                    <Badge className="bg-green-100 text-green-700 border-0">Calificado</Badge>
                  </div>
                  {entregas[0]?.comentario_docente && (
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                      <strong>Devolución:</strong> {entregas[0].comentario_docente}
                    </p>
                  )}
                  {/* Mostrar lo que entregó el estudiante */}
                  {entregas[0]?.contenido && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Tu respuesta:</p>
                      <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap">{entregas[0].contenido}</div>
                    </div>
                  )}
                </div>
              ) : t.mi_entrega?.estado === 'entregado' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Entregado el {formatFechaCorta(t.mi_entrega.fecha_entrega)}</span>
                  </div>
                  {/* Mostrar lo que entregó */}
                  {entregas[0]?.contenido && (
                    <div className="border-t pt-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Tu respuesta:</p>
                      <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap">{entregas[0].contenido}</div>
                    </div>
                  )}
                </div>
              ) : puedeEntregar ? (
                <div className="space-y-3" data-testid="submission-form">
                  {t.mi_entrega?.estado === 'devuelto' && (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                      <RotateCcw className="w-4 h-4" />
                      Tu entrega fue devuelta para corrección. Podés reenviar.
                    </div>
                  )}
                  <textarea
                    className="w-full border rounded-md px-3 py-2 text-sm min-h-[120px] resize-y"
                    placeholder="Escribí tu respuesta o entrega acá..."
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    data-testid="submission-text-input"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={entregarTarea}
                      disabled={!submissionText.trim() || submitting}
                      data-testid="submit-assignment-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      Entregar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {t.vencida && !t.permite_entrega_tardia
                    ? 'La fecha de entrega ya venció.'
                    : 'No podés entregar en este momento.'
                  }
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Teacher/Coordinator: all submissions */}
        {esCoordinadorODocente && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-5 h-5" />
                Entregas ({entregas.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingEntregas ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                </div>
              ) : entregas.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  Todavía no hay entregas para esta tarea
                </div>
              ) : (
                <div className="divide-y">
                  {entregas.map(e => (
                    <div key={e.id} className="p-4" data-testid={`submission-${e.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-semibold text-xs">
                            {(e.usuario?.nombre?.[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <span className="text-sm font-medium">{e.usuario?.nombre} {e.usuario?.apellido}</span>
                            <p className="text-xs text-gray-400">{formatFechaCorta(e.fecha_entrega)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {e.estado === 'calificado' ? (
                            <Badge className="bg-green-100 text-green-700 border-0">
                              {e.nota}/{t.puntos_maximo}
                            </Badge>
                          ) : e.estado === 'devuelto' ? (
                            <Badge className="bg-amber-100 text-amber-700 border-0">Devuelto</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 border-0">Pendiente</Badge>
                          )}
                        </div>
                      </div>

                      {/* Submission content */}
                      {e.contenido && (
                        <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap mb-3">
                          {e.contenido}
                        </div>
                      )}

                      {e.archivos?.length > 0 && (
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {e.archivos.map((a, i) => (
                            <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded">
                              <Paperclip className="w-3 h-3" />{a.nombre || 'Archivo'}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Grading area */}
                      {gradingId === e.id ? (
                        <div className="flex items-end gap-2 mt-2 bg-violet-50 p-3 rounded-md" data-testid={`grade-form-${e.id}`}>
                          <div className="flex-shrink-0">
                            <label className="text-xs font-medium text-gray-600">Nota (0-{t.puntos_maximo})</label>
                            <Input
                              type="number"
                              min="0"
                              max={t.puntos_maximo}
                              value={gradeForm.nota}
                              onChange={(ev) => setGradeForm({ ...gradeForm, nota: ev.target.value })}
                              className="w-20 h-8 text-sm"
                              data-testid={`grade-input-${e.id}`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600">Devolución (opcional)</label>
                            <Input
                              placeholder="Comentario..."
                              value={gradeForm.comentario}
                              onChange={(ev) => setGradeForm({ ...gradeForm, comentario: ev.target.value })}
                              className="h-8 text-sm"
                              data-testid={`grade-comment-${e.id}`}
                            />
                          </div>
                          <Button size="sm" onClick={() => calificarEntrega(e.id)} disabled={grading}
                            className="h-8" data-testid={`grade-submit-${e.id}`}>
                            {grading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Calificar'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setGradingId(null)} className="h-8">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-2">
                          {e.estado !== 'calificado' && (
                            <Button size="sm" variant="outline" onClick={() => { setGradingId(e.id); setGradeForm({ nota: '', comentario: '' }); }}
                              data-testid={`open-grade-${e.id}`}>
                              <Star className="w-3.5 h-3.5 mr-1" />Calificar
                            </Button>
                          )}
                          {e.estado === 'calificado' && (
                            <Button size="sm" variant="ghost" onClick={() => devolverEntrega(e.id)} data-testid={`return-${e.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />Devolver
                            </Button>
                          )}
                          {e.estado === 'calificado' && (
                            <Button size="sm" variant="outline" onClick={() => { setGradingId(e.id); setGradeForm({ nota: String(e.nota || ''), comentario: e.comentario_docente || '' }); }}>
                              <Star className="w-3.5 h-3.5 mr-1" />Recalificar
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Show existing grade comment */}
                      {e.estado === 'calificado' && e.comentario_docente && gradingId !== e.id && (
                        <p className="text-xs text-gray-500 mt-2 italic">Devolución: {e.comentario_docente}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // --- List View ---
  return (
    <div className="space-y-4">
      {esCoordinadorODocente && (
        <div className="flex justify-between items-center">
          <div />
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="create-assignment-btn">
            <Plus className="w-4 h-4 mr-1" />
            Nueva Tarea
          </Button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <Card data-testid="assignment-form">
          <CardContent className="p-4 space-y-3">
            <Input
              placeholder="Título de la tarea"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              data-testid="assignment-title-input"
            />
            <textarea
              className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] resize-y"
              placeholder="Descripción e instrucciones..."
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              data-testid="assignment-desc-input"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Tipo</label>
                <select className="w-full border rounded-md px-2 py-1.5 text-sm" value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="assignment-type-select">
                  <option value="tarea">Tarea</option>
                  <option value="trabajo_practico">Trabajo Práctico</option>
                  <option value="informe">Informe</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Puntos</label>
                <Input type="number" min="1" max="1000" value={form.puntos_maximo}
                  onChange={(e) => setForm({ ...form, puntos_maximo: e.target.value })}
                  className="h-8 text-sm" data-testid="assignment-points-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Vencimiento</label>
                <Input type="datetime-local" value={form.fecha_vencimiento}
                  onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
                  className="h-8 text-sm" data-testid="assignment-due-date" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.permite_entrega_tardia}
                    onChange={(e) => setForm({ ...form, permite_entrega_tardia: e.target.checked })} />
                  Entrega tardía
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button size="sm" onClick={crearTarea} disabled={saving || !form.titulo.trim()} data-testid="save-assignment-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Crear Tarea
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        </div>
      )}

      {/* Empty state */}
      {!loading && tareas.length === 0 && (
        <Card className="text-center py-8">
          <CardContent>
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay tareas en este espacio</p>
          </CardContent>
        </Card>
      )}

      {/* Tareas list */}
      {!loading && tareas.map(tarea => (
        <Card
          key={tarea.id}
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openTarea(tarea)}
          data-testid={`tarea-card-${tarea.id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="outline" className="text-xs">{TIPO_LABELS[tarea.tipo] || tarea.tipo}</Badge>
                  <EstadoBadge estado={tarea.estado} vencida={tarea.vencida} />
                  {tarea.mi_entrega && (
                    <EntregaBadge estado={tarea.mi_entrega.estado} nota={tarea.mi_entrega.nota} max={tarea.puntos_maximo} />
                  )}
                </div>
                <h3 className="font-semibold text-gray-900">{tarea.titulo}</h3>
                {tarea.descripcion && (
                  <p className="text-sm text-gray-500 line-clamp-2 mt-1">{tarea.descripcion}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  {tarea.fecha_vencimiento && (
                    <span className={`flex items-center gap-1 ${tarea.vencida ? 'text-red-500' : ''}`}>
                      <Clock className="w-3 h-3" />
                      {formatFechaCorta(tarea.fecha_vencimiento)}
                    </span>
                  )}
                  <span>{tarea.puntos_maximo} pts</span>
                  <span>{tarea.total_entregas} entrega(s)</span>
                </div>
              </div>
              {esCoordinadorODocente && (
                <button
                  onClick={(e) => { e.stopPropagation(); eliminarTarea(tarea.id); }}
                  className="text-gray-300 hover:text-red-500 p-1 ml-2"
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --- Helper Components ---

function EstadoBadge({ estado, vencida }) {
  if (estado === 'borrador') return <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">Borrador</Badge>;
  if (estado === 'cerrada') return <Badge className="bg-red-100 text-red-600 border-0 text-xs">Cerrada</Badge>;
  if (vencida) return <Badge className="bg-red-100 text-red-600 border-0 text-xs">Vencida</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Abierta</Badge>;
}

function EntregaBadge({ estado, nota, max }) {
  if (estado === 'calificado') {
    return <Badge className="bg-violet-100 text-violet-700 border-0 text-xs">{nota}/{max}</Badge>;
  }
  if (estado === 'devuelto') {
    return <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Devuelto</Badge>;
  }
  return <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">Entregado</Badge>;
}
